(function () {
  const DRAWER_ROOT = '#nudge-offer-drawer-icu';
  const CAROUSEL_SELECTOR = `${DRAWER_ROOT} .nudge-offer.carousel`;
  const ADD_BUTTON_SELECTOR = `${DRAWER_ROOT} input.bttn.product-price[name="add"]`;
  const LOADING_LABEL = 'ADDING..';
  const formatCache = new Map();
  const loadingFallbacks = new WeakMap();
  let resetLoadingTimer;

  function getShopifyRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function parseProductHandle(link) {
    if (!link) return null;
    try {
      const pathname = new URL(link.href, window.location.origin).pathname;
      const match = pathname.match(/\/products\/([^/]+)/);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  }

  function parsePriceForButton(priceStr) {
    if (!priceStr) return '';
    const match = String(priceStr).match(/\$[\d,.]+/);
    return match ? match[0] : String(priceStr).trim().split(/\s+/)[0];
  }

  function updateAddButtonPrice(wrapper) {
    const select = wrapper.querySelector('select[id^="ajax-product-select-"]');
    const button = wrapper.querySelector('input.bttn.product-price');
    if (!select || !button) return;

    const selectedOption = select.options[select.selectedIndex];
    const price = parsePriceForButton(
      selectedOption?.dataset?.variantPrice || selectedOption?.getAttribute('data-variant-price')
    );
    const label = price ? `ADD ${price}` : 'ADD';
    button.dataset.icuAddLabel = label;
    if (!button.classList.contains('icu-add--loading')) {
      button.value = label;
    }
  }

  function clearLoadingFallback(button) {
    const timeoutId = loadingFallbacks.get(button);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      loadingFallbacks.delete(button);
    }
  }

  function setAddButtonLoading(button, isLoading) {
    if (!button) return;

    if (isLoading) {
      if (!button.dataset.icuAddLabel) {
        button.dataset.icuAddLabel = button.value || 'ADD';
      }
      button.value = LOADING_LABEL;
      button.classList.add('icu-add--loading');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      return;
    }

    const label = button.dataset.icuAddLabel || 'ADD';
    button.value = label;
    button.classList.remove('icu-add--loading');
    button.disabled = false;
    button.removeAttribute('aria-busy');
    clearLoadingFallback(button);
  }

  function scheduleLoadingFallback(button) {
    clearLoadingFallback(button);
    loadingFallbacks.set(
      button,
      window.setTimeout(() => setAddButtonLoading(button, false), 10000)
    );
  }

  function hasLoadingUpsellButton() {
    return Boolean(document.querySelector(`${ADD_BUTTON_SELECTOR}.icu-add--loading`));
  }

  function resetUpsellAddButtonsLoading() {
    document.querySelectorAll(`${ADD_BUTTON_SELECTOR}.icu-add--loading`).forEach((button) => {
      setAddButtonLoading(button, false);
      const slide = button.closest('.product-wrapper');
      if (slide) updateAddButtonPrice(slide);
    });
  }

  function scheduleResetUpsellLoading() {
    if (!hasLoadingUpsellButton()) return;
    window.clearTimeout(resetLoadingTimer);
    resetLoadingTimer = window.setTimeout(() => {
      requestAnimationFrame(resetUpsellAddButtonsLoading);
    }, 0);
  }

  function notifyCartUpdatedAfterUpsellAdd() {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'icu-upsell' });
      }
    } catch (e) {}
  }

  function getFetchUrl(input) {
    if (typeof input === 'string') return input;
    if (input instanceof Request) return input.url;
    return String(input?.url || '');
  }

  function isCartAddUrl(url) {
    return /\/cart\/add(\.js|\.json)?(\?|$)/i.test(url);
  }

  function isCartDrawerRefreshUrl(url) {
    return /[?&]section_id=cart-drawer\b/i.test(url) || /\/cart\?view=drawer\b/i.test(url);
  }

  function bindCartNetworkHooks() {
    if (window.__icuCartNetworkBound) return;
    window.__icuCartNetworkBound = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = function fetchWithIcuReset(input, init) {
      const url = getFetchUrl(input);
      const shouldWatch =
        hasLoadingUpsellButton() && (isCartAddUrl(url) || isCartDrawerRefreshUrl(url));

      return originalFetch(input, init)
        .then((response) => {
          if (shouldWatch) {
            scheduleResetUpsellLoading();
            if (response.ok && isCartAddUrl(url)) {
              notifyCartUpdatedAfterUpsellAdd();
            }
          }
          return response;
        })
        .catch((error) => {
          if (shouldWatch) {
            scheduleResetUpsellLoading();
          }
          throw error;
        });
    };

    const xhrOpen = XMLHttpRequest.prototype.open;
    const xhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function openWithIcuUrl(method, url, ...rest) {
      this.__icuRequestUrl = String(url || '');
      return xhrOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function sendWithIcuReset(...args) {
      const url = this.__icuRequestUrl || '';
      const shouldWatch =
        hasLoadingUpsellButton() && (isCartAddUrl(url) || isCartDrawerRefreshUrl(url));

      if (shouldWatch) {
        this.addEventListener(
          'loadend',
          () => {
            scheduleResetUpsellLoading();
            if (isCartAddUrl(url) && this.status >= 200 && this.status < 300) {
              notifyCartUpdatedAfterUpsellAdd();
            }
          },
          { once: true }
        );
      }

      return xhrSend.apply(this, args);
    };
  }

  function bindCartDrawerMutationReset() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || cartDrawer.dataset.icuDrawerResetBound === 'true') return;

    cartDrawer.dataset.icuDrawerResetBound = 'true';

    const lineItemsObserver = new MutationObserver(() => {
      scheduleResetUpsellLoading();
    });

    const watchLineItemTargets = () => {
      cartDrawer.querySelectorAll('cart-drawer-items, #CartDrawer-Form .cart-items tbody').forEach((node) => {
        if (node.dataset.icuLineItemsObserved === 'true') return;
        node.dataset.icuLineItemsObserved = 'true';
        lineItemsObserver.observe(node, { childList: true, subtree: true });
      });
    };

    watchLineItemTargets();

    new MutationObserver(watchLineItemTargets).observe(cartDrawer, {
      childList: true,
      subtree: true,
    });
  }

  function bindUpsellAddButtonLoading() {
    if (document.documentElement.dataset.icuLoadingBound === 'true') return;
    document.documentElement.dataset.icuLoadingBound = 'true';

    bindCartNetworkHooks();
    bindCartDrawerMutationReset();

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.closest(DRAWER_ROOT)) return;

        const button = form.querySelector('input.bttn.product-price[name="add"]');
        if (!button || button.classList.contains('icu-add--loading')) return;

        setAddButtonLoading(button, true);
        scheduleLoadingFallback(button);
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest(ADD_BUTTON_SELECTOR);
        if (!button || button.classList.contains('icu-add--loading')) return;

        setAddButtonLoading(button, true);
        scheduleLoadingFallback(button);
      },
      true
    );

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleResetUpsellLoading);
      subscribe(PUB_SUB_EVENTS.cartError, scheduleResetUpsellLoading);
    }
  }

  async function fetchProductFormat(handle) {
    if (!handle) return '';
    if (formatCache.has(handle)) return formatCache.get(handle);

    try {
      const response = await fetch(
        `${getShopifyRoot()}products/${encodeURIComponent(handle)}?sections=icu-product-format`
      );
      if (!response.ok) throw new Error('format fetch failed');

      const payload = await response.json();
      const sectionHtml = payload['icu-product-format'] || '';
      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const format =
        doc.querySelector('[data-format]')?.getAttribute('data-format')?.trim() || '';

      formatCache.set(handle, format);
      return format;
    } catch {
      formatCache.set(handle, '');
      return '';
    }
  }

  async function injectProductFormat(wrapper) {
    const link = wrapper.querySelector('.details > a[href*="/products/"]');
    const titleWrapper = wrapper.querySelector('.product-title-wrapper');
    if (!titleWrapper) return;

    const handle = parseProductHandle(link);
    const format = await fetchProductFormat(handle);

    let formatEl = wrapper.querySelector('.icu-product-format');
    if (!format) {
      formatEl?.remove();
      titleWrapper.classList.add('product-title-wrapper--no-format');
      return;
    }

    titleWrapper.classList.remove('product-title-wrapper--no-format');

    if (!formatEl) {
      formatEl = document.createElement('div');
      formatEl.className = 'icu-product-format';
      titleWrapper.insertAdjacentElement('afterend', formatEl);
    }

    formatEl.textContent = format;
  }

  function cleanProductTitles(root) {
    root.querySelectorAll('.product-title').forEach((titleEl) => {
      if (titleEl.innerHTML.includes('&nbsp;')) {
        titleEl.innerHTML = titleEl.innerHTML.replace(/&nbsp;/g, '');
      }
    });
  }

  function alignOfferDetails(slide) {
    slide.style.textAlign = 'left';

    slide.querySelectorAll('.product-image-wrapper, .product-image-wrapper > div').forEach((el) => {
      el.style.textAlign = 'left';
      el.style.justifyContent = 'flex-start';
    });

    const details = slide.querySelector('.details');
    if (!details) return;

    details.style.textAlign = 'left';

    details.querySelectorAll('a, .product-title-wrapper, .product-title, form').forEach((el) => {
      el.style.textAlign = 'left';
      if (el.tagName === 'A') {
        el.style.display = 'block';
        el.style.verticalAlign = 'top';
      }
    });
  }

  function getCarouselSlides(carousel) {
    const offerCollection = carousel.querySelector('.offer-collection');
    if (!offerCollection) return [];
    return [...offerCollection.querySelectorAll('.product-wrapper')];
  }

  function getActiveSlideIndex(slides) {
    const index = slides.findIndex((slide) => slide.classList.contains('active'));
    return index >= 0 ? index : 0;
  }

  function goToSlide(carousel, index) {
    const slides = getCarouselSlides(carousel);
    if (!slides.length) return;

    const targetIndex = ((index % slides.length) + slides.length) % slides.length;
    carousel.dataset.icuSlideSyncing = 'true';
    slides.forEach((item, itemIndex) => {
      item.classList.toggle('active', itemIndex === targetIndex);
      item.style.marginLeft = itemIndex === targetIndex ? '0%' : '';
    });
    syncCarouselNav(carousel);
    window.requestAnimationFrame(() => {
      delete carousel.dataset.icuSlideSyncing;
    });
  }

  function goToNextSlide(carousel) {
    const slides = getCarouselSlides(carousel);
    goToSlide(carousel, getActiveSlideIndex(slides) + 1);
  }

  function goToPrevSlide(carousel) {
    const slides = getCarouselSlides(carousel);
    goToSlide(carousel, getActiveSlideIndex(slides) - 1);
  }

  function syncCarouselNav(carousel) {
    const slides = getCarouselSlides(carousel);
    if (!slides.length) return;

    carousel.querySelector('.icu-carousel-dots')?.remove();

    let nav = carousel.querySelector('.icu-carousel-nav');
    if (!nav) {
      nav = document.createElement('div');
      nav.className = 'icu-carousel-nav';

      const prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'icu-carousel-nav__btn icu-carousel-nav__btn--prev';
      prevBtn.setAttribute('aria-label', 'Previous offer');
      prevBtn.addEventListener('click', () => goToPrevSlide(carousel));

      const counter = document.createElement('span');
      counter.className = 'icu-carousel-nav__counter';

      const nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'icu-carousel-nav__btn icu-carousel-nav__btn--next';
      nextBtn.setAttribute('aria-label', 'Next offer');
      nextBtn.addEventListener('click', () => goToNextSlide(carousel));

      nav.append(prevBtn, counter, nextBtn);
      carousel.appendChild(nav);
    }

    const activeIndex = getActiveSlideIndex(slides);
    const counter = nav.querySelector('.icu-carousel-nav__counter');
    const counterText = `${activeIndex + 1} / ${slides.length}`;
    if (counter && counter.textContent !== counterText) {
      counter.textContent = counterText;
    }

    const shouldHideNav = slides.length < 2;
    if (nav.hidden !== shouldHideNav) {
      nav.hidden = shouldHideNav;
    }
  }

  function isInteractiveCarouselTarget(target) {
    return Boolean(target?.closest('a, button, input, select, label, textarea'));
  }

  function bindCarouselGestures(carousel) {
    const offerCollection = carousel.querySelector('.offer-collection');
    if (!offerCollection || offerCollection.dataset.icuSwipeBound === 'true') return;

    const slides = getCarouselSlides(carousel);
    if (slides.length < 2) return;

    offerCollection.dataset.icuSwipeBound = 'true';

    const SWIPE_THRESHOLD = 40;
    const AXIS_LOCK_THRESHOLD = 12;
    const WHEEL_THRESHOLD = 18;
    let startX = 0;
    let startY = 0;
    let isTracking = false;
    let isHorizontalSwipe = false;
    let pointerDragging = false;
    let wheelLocked = false;

    const handleSwipeDelta = (deltaX, deltaY) => {
      if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) return;

      if (deltaX < 0) {
        goToNextSlide(carousel);
      } else {
        goToPrevSlide(carousel);
      }
    };

    offerCollection.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        isTracking = true;
        isHorizontalSwipe = false;
      },
      { passive: true }
    );

    offerCollection.addEventListener(
      'touchmove',
      (event) => {
        if (!isTracking || event.touches.length !== 1) return;

        const deltaX = event.touches[0].clientX - startX;
        const deltaY = event.touches[0].clientY - startY;

        if (
          !isHorizontalSwipe &&
          Math.abs(deltaX) > AXIS_LOCK_THRESHOLD &&
          Math.abs(deltaX) > Math.abs(deltaY)
        ) {
          isHorizontalSwipe = true;
        }

        if (isHorizontalSwipe) {
          event.preventDefault();
        }
      },
      { passive: false }
    );

    offerCollection.addEventListener(
      'touchend',
      (event) => {
        if (!isTracking) return;
        isTracking = false;

        const touch = event.changedTouches[0];
        handleSwipeDelta(touch.clientX - startX, touch.clientY - startY);
      },
      { passive: true }
    );

    // Mouse drag (click and pull on non-interactive areas)
    offerCollection.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      if (isInteractiveCarouselTarget(event.target)) return;

      pointerDragging = true;
      startX = event.clientX;
      startY = event.clientY;
      offerCollection.setPointerCapture(event.pointerId);
    });

    offerCollection.addEventListener('pointerup', (event) => {
      if (event.pointerType === 'touch' || !pointerDragging) return;
      pointerDragging = false;

      handleSwipeDelta(event.clientX - startX, event.clientY - startY);
    });

    offerCollection.addEventListener('pointercancel', () => {
      pointerDragging = false;
    });

    // Trackpad / mouse wheel horizontal scroll (primary laptop gesture)
    offerCollection.addEventListener(
      'wheel',
      (event) => {
        if (Math.abs(event.deltaX) < Math.abs(event.deltaY)) return;
        if (Math.abs(event.deltaX) < WHEEL_THRESHOLD) return;
        if (wheelLocked) return;

        event.preventDefault();
        wheelLocked = true;
        window.setTimeout(() => {
          wheelLocked = false;
        }, 350);

        if (event.deltaX > 0) {
          goToNextSlide(carousel);
        } else {
          goToPrevSlide(carousel);
        }
      },
      { passive: false }
    );
  }

  async function enhanceSlide(slide) {
    if (slide.dataset.icuEnhanced === 'true') return;

    slide.dataset.icuEnhanced = 'true';
    alignOfferDetails(slide);
    await injectProductFormat(slide);
    updateAddButtonPrice(slide);

    const select = slide.querySelector('select[id^="ajax-product-select-"]');
    if (select && !select.dataset.icuPriceBound) {
      select.dataset.icuPriceBound = 'true';
      select.addEventListener('change', () => updateAddButtonPrice(slide));
    }
  }

  function isEmptyCartCarousel(carousel) {
    return Boolean(carousel.closest('cart-drawer.is-empty'));
  }

  async function enhanceCarousel(carousel) {
    cleanProductTitles(carousel);

    const slides = carousel.querySelectorAll('.product-wrapper');
    await Promise.all([...slides].map((slide) => enhanceSlide(slide)));

    if (isEmptyCartCarousel(carousel)) {
      slides.forEach((slide) => {
        slide.classList.add('active');
        slide.style.marginLeft = '';
      });
      carousel.querySelector('.icu-carousel-dots')?.remove();
      carousel.querySelector('.icu-carousel-nav')?.remove();
      return;
    }

    syncCarouselNav(carousel);
    bindCarouselGestures(carousel);

    const offerCollection = carousel.querySelector('.offer-collection');
    if (offerCollection && !offerCollection.dataset.icuNavObserved) {
      offerCollection.dataset.icuNavObserved = 'true';
      let navSyncTimer;
      const slideObserver = new MutationObserver(() => {
        if (carousel.dataset.icuSlideSyncing === 'true') return;
        window.clearTimeout(navSyncTimer);
        navSyncTimer = window.setTimeout(() => syncCarouselNav(carousel), 50);
      });
      slideObserver.observe(offerCollection, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        subtree: true,
        childList: true,
      });
    }
  }

  function enhanceIcuCartDrawer(root = document) {
    root.querySelectorAll(CAROUSEL_SELECTOR).forEach((carousel) => {
      enhanceCarousel(carousel);
    });
  }

  function init() {
    bindUpsellAddButtonLoading();
    enhanceIcuCartDrawer();

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    let debounceTimer;
    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => enhanceIcuCartDrawer(cartDrawer), 150);
    });

    observer.observe(cartDrawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
